/* eslint-disable no-nested-ternary */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Container,
  Typography,
  Stack,
  Button,
  Box,
  CircularProgress,
  Divider,
  Card,
  Grid,
  Paper,
  Chip,
  Avatar,
  LinearProgress,
  Tabs,
  Tab,
  InputAdornment,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Skeleton,
  Checkbox,
  FormControlLabel,
  RadioGroup,
  Radio,
  FormLabel,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import DateRangeIcon from '@mui/icons-material/DateRange';
import AssessmentIcon from '@mui/icons-material/Assessment';
import PrintIcon from '@mui/icons-material/Print';
import PaymentsIcon from '@mui/icons-material/Payments';
import TimelineIcon from '@mui/icons-material/Timeline';
import SpeedIcon from '@mui/icons-material/Speed';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

import ReactToPrint from 'react-to-print';
import { toast } from 'react-toastify';
import { LocalizationProvider, DateRangePicker } from '@mui/x-date-pickers-pro';
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs';

import Page from '../components/Page';
import { ComponentToPrint } from './components/PrintComponent';
import ProcessorReportTable from './components/ProcessorTable';
import { fCurrency } from '../utils/formatNumber';
import {
  generateAdminTransactionReport,
  generateProfitAndLossReport,
  getTopUsersAnalysis,
  getInactiveUsersAnalysis,
} from '../api/transactions.api';

function padTo2Digits(num) {
  return num.toString().padStart(2, '0');
}

function formatDate(date) {
  return [date.getFullYear(), padTo2Digits(date.getMonth() + 1), padTo2Digits(date.getDate())].join('-');
}

// STAT CARD Component
function StatCard({ title, value, subtitle, icon, color = '#1c4e5d' }) {
  const isOpening = title.toLowerCase().includes('opening');
  const isClosing = title.toLowerCase().includes('closing');
  const isMoneyIn = title.toLowerCase().includes('in');
  const isMoneyOut = title.toLowerCase().includes('out');

  let accentColor = '#1c4e5d'; // Default Teal
  let lightAccent = 'rgba(28, 78, 93, 0.06)';
  if (isMoneyIn) {
    accentColor = '#15803d'; // Green
    lightAccent = 'rgba(21, 128, 61, 0.08)';
  } else if (isMoneyOut) {
    accentColor = '#b91c1c'; // Red
    lightAccent = 'rgba(185, 28, 28, 0.08)';
  } else if (isOpening || isClosing) {
    accentColor = '#3366FF'; // Blue
    lightAccent = 'rgba(51, 102, 255, 0.08)';
  }

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 3.5,
        border: '1px solid',
        borderColor: 'grey.200',
        height: '100%',
        background: 'linear-gradient(135deg, #ffffff 0%, #fafbfc 100%)',
        position: 'relative',
        overflow: 'hidden',
        boxShadow: '0 2px 16px 0 rgba(0, 0, 0, 0.02)',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 12px 24px -10px rgba(0, 0, 0, 0.08)',
          borderColor: accentColor,
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          width: '4px',
          height: '100%',
          backgroundColor: accentColor,
        },
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
        <Box>
          <Typography variant="body2" color="text.secondary" fontWeight={600} mb={1}>
            {title}
          </Typography>
          <Typography variant="h4" fontWeight={800} sx={{ color: color || accentColor, letterSpacing: '-0.5px' }}>
            {value}
          </Typography>
          {subtitle ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, fontWeight: 500 }}>
              {subtitle}
            </Typography>
          ) : null}
        </Box>

        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: '12px',
            backgroundColor: lightAccent,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: accentColor,
            boxShadow: `0 4px 10px 0 ${lightAccent}`,
          }}
        >
          {icon}
        </Box>
      </Stack>
    </Paper>
  );
}

// Row component for list values
function Row({ label, value }) {
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2} py={1}>
      <Typography variant="body2" color="text.secondary" fontWeight={500}>
        {label}
      </Typography>
      <Typography fontWeight={700} textAlign="right" color="text.primary">
        {value}
      </Typography>
    </Stack>
  );
}

// Mini metric card component
function MiniMetric({ title, value }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'grey.200',
        height: '100%',
        backgroundColor: '#ffffff',
        boxShadow: '0 2px 8px 0 rgba(0, 0, 0, 0.01)',
        transition: 'all 0.25s ease',
        '&:hover': {
          borderColor: 'primary.main',
          boxShadow: '0 6px 16px 0 rgba(28, 78, 93, 0.06)',
          transform: 'translateY(-2px)',
        },
      }}
    >
      <Typography
        variant="caption"
        color="text.secondary"
        fontWeight={600}
        display="block"
        mb={0.8}
        sx={{ textTransform: 'uppercase', letterSpacing: '0.5px' }}
      >
        {title}
      </Typography>
      <Typography variant="h6" fontWeight={800} color="primary.main">
        {value}
      </Typography>
    </Paper>
  );
}

// Empty State: Report
function EmptyReportState({ value, handleAction, loading, actionLabel, description, iconColor = 'primary.main' }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 6,
        borderRadius: 4,
        border: '2px dashed',
        borderColor: 'grey.300',
        backgroundColor: 'grey.50',
        textAlign: 'center',
        background: 'linear-gradient(180deg, rgba(255,255,255,0.6) 0%, rgba(244,246,248,0.6) 100%)',
        backdropFilter: 'blur(8px)',
        transition: 'all 0.3s ease',
        '&:hover': {
          borderColor: 'primary.light',
        },
      }}
    >
      <Stack spacing={3} alignItems="center" maxWidth={480} sx={{ mx: 'auto' }}>
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: '50%',
            backgroundColor: 'primary.lighter',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: iconColor,
            boxShadow: '0 8px 16px 0 rgba(28, 78, 93, 0.1)',
          }}
        >
          <AssessmentIcon sx={{ fontSize: 40 }} />
        </Box>

        <Box>
          <Typography variant="h5" fontWeight={700} gutterBottom>
            No Analytics Data Loaded
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {description}
          </Typography>
        </Box>

        {!value[0] || !value[1] ? (
          <Chip
            icon={<DateRangeIcon />}
            label="Please select a date range above to start"
            color="warning"
            variant="outlined"
            sx={{ fontWeight: 600, px: 1, height: 32 }}
          />
        ) : (
          <Button
            onClick={handleAction}
            variant="contained"
            color="primary"
            disabled={loading}
            sx={{ borderRadius: 2.5, fontWeight: 700, px: 4 }}
          >
            {loading ? 'Running...' : actionLabel}
          </Button>
        )}
      </Stack>
    </Paper>
  );
}

// Empty State: Leaderboard
function EmptyLeaderboardState({ handleFetchLeaderboard, leaderboardLoading }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 6,
        borderRadius: 4,
        border: '2px dashed',
        borderColor: 'grey.300',
        backgroundColor: 'grey.50',
        textAlign: 'center',
        background: 'linear-gradient(180deg, rgba(255,255,255,0.6) 0%, rgba(244,246,248,0.6) 100%)',
        backdropFilter: 'blur(8px)',
        transition: 'all 0.3s ease',
        '&:hover': {
          borderColor: 'warning.light',
        },
      }}
    >
      <Stack spacing={3} alignItems="center" maxWidth={480} sx={{ mx: 'auto' }}>
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: '50%',
            backgroundColor: 'warning.lighter',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'warning.dark',
            boxShadow: '0 8px 16px 0 rgba(255, 193, 7, 0.15)',
          }}
        >
          <EmojiEventsIcon sx={{ fontSize: 40 }} />
        </Box>

        <Box>
          <Typography variant="h5" fontWeight={700} gutterBottom>
            Leaderboard Analysis Ready
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Analyze your top-performing users sorted by transaction volume or referred users count. You can apply
            filters and search terms first.
          </Typography>
        </Box>

        <Button
          onClick={handleFetchLeaderboard}
          variant="contained"
          color="warning"
          disabled={leaderboardLoading}
          sx={{ borderRadius: 2.5, fontWeight: 700, color: 'grey.900', px: 4 }}
        >
          {leaderboardLoading ? 'Loading...' : 'Analyze Top Users'}
        </Button>
      </Stack>
    </Paper>
  );
}

// Empty State: Inactive Users
function EmptyInactiveState({ handleFetchInactive, inactiveLoading }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 6,
        borderRadius: 4,
        border: '2px dashed',
        borderColor: 'grey.300',
        backgroundColor: 'grey.50',
        textAlign: 'center',
        background: 'linear-gradient(180deg, rgba(255,255,255,0.6) 0%, rgba(244,246,248,0.6) 100%)',
        backdropFilter: 'blur(8px)',
        transition: 'all 0.3s ease',
        '&:hover': {
          borderColor: 'error.light',
        },
      }}
    >
      <Stack spacing={3} alignItems="center" maxWidth={480} sx={{ mx: 'auto' }}>
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: '50%',
            backgroundColor: 'error.lighter',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'error.dark',
            boxShadow: '0 8px 16px 0 rgba(211, 47, 47, 0.15)',
          }}
        >
          <PeopleAltIcon sx={{ fontSize: 40 }} />
        </Box>

        <Box>
          <Typography variant="h5" fontWeight={700} gutterBottom>
            Inactive Users Analysis
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Identify registered users who have not processed any transactions during a specific number of days, helping
            you run target retention campaigns.
          </Typography>
        </Box>

        <Button
          onClick={handleFetchInactive}
          variant="contained"
          color="error"
          disabled={inactiveLoading}
          sx={{ borderRadius: 2.5, fontWeight: 700, color: 'white', px: 4 }}
        >
          {inactiveLoading ? 'Analyzing...' : 'Fetch Inactive Users'}
        </Button>
      </Stack>
    </Paper>
  );
}

// Report Loading Skeleton
function ReportSkeleton() {
  return (
    <Stack spacing={3}>
      <Grid container spacing={2}>
        {[1, 2, 3, 4].map((i) => (
          <Grid item xs={12} sm={6} md={3} key={i}>
            <Paper elevation={0} sx={{ p: 3, borderRadius: 3.5, border: '1px solid', borderColor: 'grey.200' }}>
              <Stack direction="row" justifyContent="space-between" spacing={2}>
                <Box sx={{ width: '65%' }}>
                  <Skeleton variant="text" sx={{ fontSize: '0.8rem', mb: 1 }} />
                  <Skeleton variant="text" sx={{ fontSize: '1.8rem', width: '80%', mb: 0.5 }} />
                  <Skeleton variant="text" sx={{ fontSize: '0.7rem', width: '50%' }} />
                </Box>
                <Skeleton variant="rectangular" width={48} height={48} sx={{ borderRadius: 2.5 }} />
              </Stack>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3.5, border: '1px solid', borderColor: 'grey.200' }}>
            <Skeleton variant="text" sx={{ fontSize: '1.2rem', mb: 2, width: '40%' }} />
            <Stack spacing={2}>
              {[1, 2, 3, 4, 5].map((i) => (
                <Stack key={i} direction="row" justifyContent="space-between">
                  <Skeleton variant="text" sx={{ width: '45%' }} />
                  <Skeleton variant="text" sx={{ width: '25%' }} />
                </Stack>
              ))}
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3.5, border: '1px solid', borderColor: 'grey.200' }}>
            <Skeleton variant="text" sx={{ fontSize: '1.2rem', mb: 2, width: '40%' }} />
            <Stack spacing={2}>
              {[1, 2, 3, 4, 5].map((i) => (
                <Stack key={i} direction="row" justifyContent="space-between">
                  <Skeleton variant="text" sx={{ width: '45%' }} />
                  <Skeleton variant="text" sx={{ width: '25%' }} />
                </Stack>
              ))}
            </Stack>
          </Paper>
        </Grid>
      </Grid>
    </Stack>
  );
}

// Profit Loading Skeleton
function ProfitSkeleton() {
  return (
    <Stack spacing={3}>
      <Paper elevation={0} sx={{ p: 3.5, borderRadius: 3.5, border: '1px solid', borderColor: 'grey.200' }}>
        <Skeleton variant="text" sx={{ fontSize: '1.2rem', mb: 1, width: '20%' }} />
        <Skeleton variant="text" sx={{ fontSize: '0.8rem', mb: 3, width: '30%' }} />
        <Skeleton variant="rectangular" height={80} sx={{ borderRadius: 3, mb: 3 }} />
        <Grid container spacing={2.5}>
          {[1, 2, 3, 4, 5].map((i) => (
            <Grid item xs={12} sm={6} md={4} key={i}>
              <Paper elevation={0} sx={{ p: 2, borderRadius: 2.5, border: '1px solid', borderColor: 'grey.100' }}>
                <Skeleton variant="text" sx={{ width: '40%', mb: 0.5 }} />
                <Skeleton variant="text" sx={{ width: '60%', height: 28 }} />
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Paper>
    </Stack>
  );
}

// Leaderboard Loading Skeleton
function LeaderboardSkeleton() {
  return (
    <Stack spacing={2}>
      {[1, 2, 3, 4].map((i) => (
        <Paper key={i} elevation={0} sx={{ p: 3, borderRadius: 3.5, border: '1px solid', borderColor: 'grey.200' }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '70%' }}>
              <Skeleton variant="rectangular" width={58} height={24} sx={{ borderRadius: 1 }} />
              <Skeleton variant="circular" width={44} height={44} />
              <Box sx={{ width: '60%' }}>
                <Skeleton variant="text" sx={{ fontSize: '1rem', width: '80%' }} />
                <Skeleton variant="text" sx={{ fontSize: '0.8rem', width: '50%' }} />
              </Box>
            </Stack>
            <Box sx={{ width: '20%', display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
              <Skeleton variant="text" sx={{ fontSize: '1rem', width: '80%' }} />
              <Skeleton variant="text" sx={{ fontSize: '0.8rem', width: '60%' }} />
            </Box>
          </Stack>
          <Skeleton variant="rectangular" height={8} sx={{ borderRadius: 999, mt: 2 }} />
        </Paper>
      ))}
    </Stack>
  );
}

function getRankColor(index) {
  if (index === 0) return 'warning';
  if (index === 1) return 'secondary';
  if (index === 2) return 'info';
  return 'default';
}

export default function Analytics() {
  const componentRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [activeTab, setActiveTab] = useState('financial'); // 'financial' | 'profit' | 'leaderboard' | 'inactive'

  const [loading, setLoading] = useState(false);
  const [profitLoading, setProfitLoading] = useState(false);
  const [leaderboardLoading, setLeaderboardLoading] = useState(false);
  const [inactiveLoading, setInactiveLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  const [profitResult, setProfitResult] = useState(null);
  const [statisticResult, setStatisticResult] = useState(null);
  const [leaderboardRows, setLeaderboardRows] = useState([]);
  const [inactiveRows, setInactiveRows] = useState([]);
  const [exportUsers, setExportUsers] = useState([]);

  const [value, setValue] = useState([null, null]);

  // Leaderboard filters
  const [leaderboardType, setLeaderboardType] = useState('transactions');
  const [leaderboardPeriod, setLeaderboardPeriod] = useState('all');
  const [leaderboardLimit, setLeaderboardLimit] = useState(5);
  const [searchTerm, setSearchTerm] = useState('');
  const [leaderboardSearch, setLeaderboardSearch] = useState('');
  const [leaderboardSort, setLeaderboardSort] = useState('desc');

  // Inactive users filters
  const [inactiveDays, setInactiveDays] = useState(7);
  const [inactiveLimit, setInactiveLimit] = useState(20);
  const [inactivePage, setInactivePage] = useState(1);
  const [inactiveTotalCount, setInactiveTotalCount] = useState(0);

  // Trigger states
  const [hasFetchedLeaderboard, setHasFetchedLeaderboard] = useState(false);
  const [hasFetchedInactive, setHasFetchedInactive] = useState(false);

  const [selectedInviteUser, setSelectedInviteUser] = useState(null);
  const [inviteModalOpen, setInviteModalOpen] = useState(false);

  // Export Wizard Modal States
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [exportDelimiter, setExportDelimiter] = useState('comma'); // 'comma' | 'newline'
  const [exportSelectedFields, setExportSelectedFields] = useState({
    username: true,
    firstName: true,
    lastName: true,
    email: true,
    phone: true,
    status: false,
    createdAt: false,
  });

  const handleOpenInviteModal = (user) => {
    setSelectedInviteUser(user);
    setInviteModalOpen(true);
  };

  const handleCloseInviteModal = () => {
    setInviteModalOpen(false);
    setSelectedInviteUser(null);
  };

  const summaryCards = useMemo(() => {
    if (!statisticResult) return [];

    return [
      {
        title: 'Opening Balance',
        value: `₦${fCurrency(statisticResult.reportOpeningBalance || 0)}`,
        subtitle: 'Balance at period start',
        icon: <AccountBalanceWalletIcon />,
      },
      {
        title: 'Closing Balance',
        value: `₦${fCurrency(statisticResult.reportClosingBalance || 0)}`,
        subtitle: 'Balance at period end',
        icon: <AccountBalanceWalletIcon />,
      },
      {
        title: 'Money In',
        value: `₦${fCurrency(statisticResult.moneyIn || 0)}`,
        subtitle: 'Incoming funds',
        icon: <TrendingUpIcon />,
        color: '#15803d',
      },
      {
        title: 'Money Out',
        value: `₦${fCurrency(statisticResult.moneyOut || 0)}`,
        subtitle: 'Outgoing funds',
        icon: <ReceiptLongIcon />,
        color: '#b91c1c',
      },
    ];
  }, [statisticResult]);

  const inviteModalStats = useMemo(() => {
    const invitedUsers = selectedInviteUser?.invitedUsers || [];

    const withTransactions = invitedUsers.filter(
      (user) => Number(user?.transactionSummary?.transactions || 0) > 0
    ).length;

    const withoutTransactions = invitedUsers.length - withTransactions;

    return {
      totalInvited: invitedUsers.length,
      withTransactions,
      withoutTransactions,
    };
  }, [selectedInviteUser]);

  const leaderboardTopValue = useMemo(() => {
    if (!leaderboardRows.length) return 1;
    return leaderboardType === 'transactions' ? leaderboardRows[0]?.totalAmount || 1 : leaderboardRows[0]?.invites || 1;
  }, [leaderboardRows, leaderboardType]);

  const handleReset = () => {
    setValue([null, null]);
    setStatisticResult(null);
    setProfitResult(null);
  };

  const handleResetLeaderboardFilters = () => {
    setLeaderboardType('transactions');
    setLeaderboardPeriod('all');
    setLeaderboardLimit(5);
    setSearchTerm('');
    setLeaderboardSearch('');
    setLeaderboardSort('desc');
  };

  const handleResetInactiveFilters = () => {
    setInactiveDays(7);
    setInactiveLimit(20);
    setInactivePage(1);
  };

  const handleGenerateAdminReport = async () => {
    if (!value[0] || !value[1]) {
      toast.error('Please select a date range first.');
      return;
    }

    setLoading(true);

    try {
      const startDate = formatDate(new Date(value[0]));
      const endDate = formatDate(new Date(value[1]));

      const request = await generateAdminTransactionReport({ startDate, endDate });

      if (request?.ok) {
        setStatisticResult(request?.data?.data || null);
      } else {
        toast.error(request?.message || 'Unable to generate report.');
      }
    } catch (error) {
      console.error('Error generating report:', error);
      toast.error('Error generating report.');
    } finally {
      setLoading(false);
    }
  };

  const handleCalculateProfit = async () => {
    if (!value[0] || !value[1]) {
      toast.error('Please select a date range first.');
      return;
    }

    setProfitLoading(true);

    try {
      const startDate = formatDate(new Date(value[0]));
      const endDate = formatDate(new Date(value[1]));

      const request = await generateProfitAndLossReport({ startDate, endDate });

      if (request?.ok) {
        setProfitResult(request?.data?.data || null);
      } else {
        toast.error(request?.message || 'Unable to calculate profit.');
      }
    } catch (error) {
      console.error('Error calculating profit:', error);
      toast.error('Error calculating profit.');
    } finally {
      setProfitLoading(false);
    }
  };

  const fetchLeaderboard = useCallback(async () => {
    try {
      setLeaderboardLoading(true);

      const params = {
        type: leaderboardType,
        period: leaderboardPeriod,
        sort: leaderboardSort,
        limit: String(leaderboardLimit),
        search: leaderboardSearch,
      };

      if (value?.[0] && value?.[1]) {
        params.startDate = formatDate(new Date(value[0]));
        params.endDate = formatDate(new Date(value[1]));
      }

      const response = await getTopUsersAnalysis(params);

      if (response?.ok) {
        setLeaderboardRows(response?.data?.data || []);
      } else {
        setLeaderboardRows([]);
        toast.error(response?.message || 'Unable to fetch top users analysis.');
      }
    } catch (error) {
      console.error('fetchLeaderboard error:', error);
      setLeaderboardRows([]);
      toast.error('Unable to fetch top users analysis.');
    } finally {
      setLeaderboardLoading(false);
    }
  }, [leaderboardType, leaderboardPeriod, leaderboardSort, leaderboardLimit, leaderboardSearch, value]);

  const handleFetchLeaderboard = () => {
    setLeaderboardSearch(searchTerm);
    setHasFetchedLeaderboard(true);
  };

  useEffect(() => {
    if (hasFetchedLeaderboard) {
      fetchLeaderboard();
    }
  }, [fetchLeaderboard, hasFetchedLeaderboard]);

  // FETCH INACTIVE USERS
  const fetchInactiveUsers = useCallback(async () => {
    try {
      setInactiveLoading(true);
      const params = {
        days: String(inactiveDays),
        page: String(inactivePage),
        limit: String(inactiveLimit),
      };

      if (value?.[0] && value?.[1]) {
        params.startDate = formatDate(new Date(value[0]));
        params.endDate = formatDate(new Date(value[1]));
      }

      const response = await getInactiveUsersAnalysis(params);

      if (response?.ok) {
        setInactiveRows(response?.data?.data?.users || []);
        setInactiveTotalCount(response?.data?.data?.totalCount || 0);
      } else {
        setInactiveRows([]);
        setInactiveTotalCount(0);
        toast.error(response?.message || 'Unable to fetch inactive users.');
      }
    } catch (error) {
      console.error('fetchInactiveUsers error:', error);
      setInactiveRows([]);
      setInactiveTotalCount(0);
      toast.error('Unable to fetch inactive users.');
    } finally {
      setInactiveLoading(false);
    }
  }, [inactiveDays, inactivePage, inactiveLimit, value]);

  const handleFetchInactive = () => {
    setInactivePage(1);
    setHasFetchedInactive(true);
  };

  useEffect(() => {
    if (hasFetchedInactive) {
      fetchInactiveUsers();
    }
  }, [fetchInactiveUsers, hasFetchedInactive]);

  // EXPORT PROCESS: FETCH ALL FOR EXPORT
  const handleOpenExportModal = async () => {
    setExportModalOpen(true);
    setExportLoading(true);
    try {
      const params = {
        days: String(inactiveDays),
        page: '1',
        limit: String(inactiveTotalCount || 1000), // Request all matching users
      };

      if (value?.[0] && value?.[1]) {
        params.startDate = formatDate(new Date(value[0]));
        params.endDate = formatDate(new Date(value[1]));
      }

      const response = await getInactiveUsersAnalysis(params);
      if (response?.ok) {
        setExportUsers(response?.data?.data?.users || []);
      } else {
        toast.error('Unable to fetch users for export.');
        setExportModalOpen(false);
      }
    } catch (error) {
      console.error('Export fetch error:', error);
      toast.error('Error fetching data for export.');
      setExportModalOpen(false);
    } finally {
      setExportLoading(false);
    }
  };

  const handleCloseExportModal = () => {
    setExportModalOpen(false);
    setExportUsers([]);
  };

  const handleCheckboxChange = (field) => {
    setExportSelectedFields((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const handleSelectAllFields = (val) => {
    setExportSelectedFields({
      username: val,
      firstName: val,
      lastName: val,
      email: val,
      phone: val,
      status: val,
      createdAt: val,
    });
  };

  // GENERATE AND DOWNLOAD CSV
  const handleExportCSV = () => {
    const selectedKeys = Object.keys(exportSelectedFields).filter((key) => exportSelectedFields[key]);
    if (selectedKeys.length === 0) {
      toast.error('Please select at least one field to export.');
      return;
    }

    const headers = selectedKeys.map((k) => k.toUpperCase()).join(',');
    const rowsCsv = exportUsers.map((user) =>
      selectedKeys
        .map((key) => {
          let val = user[key] || '';
          if (key === 'createdAt') {
            val = new Date(val).toLocaleDateString();
          }
          // Escape quotes
          return `"${String(val).replace(/"/g, '""')}"`;
        })
        .join(',')
    );

    const csvContent = [headers, ...rowsCsv].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `inactive_users_${inactiveDays}_days.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('CSV downloaded successfully!');
  };

  // COPY DATA FORMATTED TO CLIPBOARD
  const handleCopyFormatted = () => {
    const selectedKeys = Object.keys(exportSelectedFields).filter((key) => exportSelectedFields[key]);
    if (selectedKeys.length === 0) {
      toast.error('Please select at least one field to copy.');
      return;
    }

    const listContent = exportUsers
      .map((user) =>
        selectedKeys
          .map((key) => {
            if (key === 'createdAt') return new Date(user[key]).toLocaleDateString();
            return user[key] || '';
          })
          .filter(Boolean)
          .join(' - ')
      )
      .join(exportDelimiter === 'comma' ? ', ' : '\n');

    navigator.clipboard.writeText(listContent);
    toast.success('Formatted data copied to clipboard!');
  };

  const salesBreakDown = statisticResult?.salesBreakDown || {};
  const status = statisticResult?.status || {};
  const processorRows = statisticResult?.result || [];

  return (
    <Page title="Analytics">
      <Container maxWidth="xl">
        <Stack spacing={3.5}>
          {/* Header Panel */}
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', md: 'center' }}
            spacing={2}
          >
            <Box>
              <Typography variant="h3" fontWeight={800} sx={{ letterSpacing: '-1px', color: 'primary.dark' }}>
                Analytics Workspace
              </Typography>
              <Typography variant="subtitle1" color="text.secondary" sx={{ fontWeight: 400 }}>
                Toggle dashboard tabs below to configure and run specific analytical reports.
              </Typography>
            </Box>
          </Stack>

          {/* iOS-like Segmented Navigation Control */}
          <Paper
            elevation={0}
            sx={{
              p: 0.6,
              borderRadius: 3,
              backgroundColor: 'grey.100',
              border: '1px solid',
              borderColor: 'grey.200',
              boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.02)',
            }}
          >
            <Tabs
              value={activeTab}
              onChange={(_, newValue) => setActiveTab(newValue)}
              variant={isMobile ? 'scrollable' : 'fullWidth'}
              scrollButtons={isMobile ? 'auto' : false}
              allowScrollButtonsMobile
              sx={{
                minHeight: 48,
                '& .MuiTabs-indicator': {
                  height: '100%',
                  borderRadius: 2.5,
                  backgroundColor: 'primary.main',
                  zIndex: 1,
                  boxShadow: '0 4px 12px 0 rgba(28, 78, 93, 0.15)',
                },
                '& .MuiTab-root': {
                  zIndex: 2,
                  textTransform: 'none',
                  fontWeight: 700,
                  fontSize: { xs: '0.85rem', md: '0.98rem' },
                  borderRadius: 2.5,
                  minHeight: 48,
                  px: { xs: 2.5, md: 3 },
                  color: 'text.secondary',
                  transition: 'color 0.2s',
                  '&.Mui-selected': {
                    color: 'common.white',
                  },
                },
              }}
            >
              <Tab value="financial" icon={<AssessmentIcon />} iconPosition="start" label="Financial Overview" />
              <Tab value="profit" icon={<PaymentsIcon />} iconPosition="start" label="Profit & Loss" />
              <Tab value="leaderboard" icon={<EmojiEventsIcon />} iconPosition="start" label="Top Users" />
              <Tab value="inactive" icon={<PeopleAltIcon />} iconPosition="start" label="Inactive Users" />
            </Tabs>
          </Paper>

          {/* Global Date Scope Card */}
          <Card
            sx={{
              borderRadius: 3.5,
              boxShadow: '0 4px 20px 0 rgba(0,0,0,0.04)',
              border: '1px solid',
              borderColor: 'grey.200',
              overflow: 'visible',
            }}
          >
            <Box p={{ xs: 3, md: 4 }}>
              <Grid container spacing={3} alignItems="center">
                <Grid item xs={12} lg={4}>
                  <Box>
                    <Typography
                      variant="h5"
                      fontWeight={800}
                      gutterBottom
                      sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                    >
                      <DateRangeIcon color="primary" /> Report Scope / Range
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Specify custom date boundaries to filter transaction logs, profit margin audits, and user
                      rankings.
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} md={7} lg={5}>
                  <LocalizationProvider dateAdapter={AdapterDayjs} localeText={{ start: 'From Date', end: 'To Date' }}>
                    <DateRangePicker
                      value={value}
                      calendars={1}
                      onChange={(newValue) => setValue(newValue)}
                      renderInput={(startProps, endProps) => (
                        <Stack direction={{ xs: 'column', sm: 'row' }} alignItems="center" spacing={2} width="100%">
                          <TextField
                            {...startProps}
                            fullWidth
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2.5 } }}
                          />
                          <Typography
                            variant="body2"
                            sx={{ color: 'text.secondary', fontWeight: 600, display: { xs: 'none', sm: 'block' } }}
                          >
                            to
                          </Typography>
                          <TextField
                            {...endProps}
                            fullWidth
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2.5 } }}
                          />
                        </Stack>
                      )}
                    />
                  </LocalizationProvider>
                </Grid>

                <Grid item xs={12} md={5} lg={3}>
                  <Button
                    fullWidth
                    onClick={handleReset}
                    variant="outlined"
                    color="inherit"
                    sx={{ height: 52, borderRadius: 2.5, fontWeight: 700 }}
                  >
                    Reset Period
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Card>

          {/* TAB 1: FINANCIAL OVERVIEW WORKSPACE */}
          {activeTab === 'financial' && (
            <Stack spacing={3.5}>
              <Card
                sx={{
                  borderRadius: 3.5,
                  boxShadow: '0 4px 20px 0 rgba(0,0,0,0.04)',
                  border: '1px solid',
                  borderColor: 'grey.200',
                }}
              >
                <Box p={3}>
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    alignItems={{ xs: 'stretch', md: 'center' }}
                    justifyContent="space-between"
                    useFlexGap
                  >
                    <Box>
                      <Typography variant="h6" fontWeight={800}>
                        Financial Audit Actions
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Compile transaction records and deposit breakdowns for the chosen range.
                      </Typography>
                    </Box>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} width={{ xs: '100%', sm: 'auto' }}>
                      <Button
                        disabled={loading || !value[0] || !value[1]}
                        onClick={handleGenerateAdminReport}
                        variant="contained"
                        startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <AssessmentIcon />}
                        sx={{ borderRadius: 2.5, width: { xs: '100%', sm: 180 }, height: 48, fontWeight: 700 }}
                      >
                        {loading ? 'Generating...' : 'Generate Report'}
                      </Button>

                      {statisticResult && !loading && (
                        <ReactToPrint
                          documentTitle={`Report-${new Date().toISOString()}`}
                          trigger={() => (
                            <Button
                              variant="contained"
                              color="secondary"
                              startIcon={<PrintIcon />}
                              sx={{ borderRadius: 2.5, width: { xs: '100%', sm: 180 }, height: 48, fontWeight: 700 }}
                            >
                              Print & Download
                            </Button>
                          )}
                          content={() => componentRef.current}
                        />
                      )}
                    </Stack>
                  </Stack>
                </Box>
              </Card>

              {/* Data Load Output */}
              {loading ? (
                <ReportSkeleton />
              ) : !statisticResult ? (
                <EmptyReportState
                  value={value}
                  handleAction={handleGenerateAdminReport}
                  loading={loading}
                  actionLabel="Generate Report"
                  description="To view detailed financial reports, automated vs manual deposit breakdowns, and transaction volumes, select a date range and request a report."
                />
              ) : (
                <ComponentToPrint ref={componentRef}>
                  <Stack spacing={3.5}>
                    {statisticResult?.message && (
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2.5,
                          borderRadius: 3,
                          border: '1px solid',
                          borderColor: 'error.light',
                          backgroundColor: 'error.lighter',
                        }}
                      >
                        <Typography color="error.dark" textAlign="center" fontWeight={700}>
                          {statisticResult.message}
                        </Typography>
                      </Paper>
                    )}

                    {/* Stat Metrics Cards */}
                    <Grid container spacing={2.5}>
                      {summaryCards.map((item) => (
                        <Grid item xs={12} sm={6} md={3} key={item?.title}>
                          <StatCard {...item} />
                        </Grid>
                      ))}
                    </Grid>

                    {/* Grid Splits for breakdowns */}
                    <Grid container spacing={3.5}>
                      <Grid item xs={12} md={6}>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 3.5,
                            borderRadius: 3.5,
                            border: '1px solid',
                            borderColor: 'grey.200',
                            height: '100%',
                            backgroundColor: '#ffffff',
                            boxShadow: '0 2px 12px 0 rgba(0, 0, 0, 0.01)',
                          }}
                        >
                          <Typography
                            variant="h5"
                            fontWeight={800}
                            mb={2.5}
                            sx={{ display: 'flex', alignItems: 'center', gap: 1.2, color: 'primary.dark' }}
                          >
                            <PaymentsIcon color="primary" /> Deposit Breakdown
                          </Typography>
                          <Stack divider={<Divider />} spacing={1.5}>
                            <Row
                              label="Number of Automated Deposits"
                              value={statisticResult.numberOfAutomatedFunding || 0}
                            />
                            <Row
                              label="Automated Deposit Amount"
                              value={`₦${fCurrency(statisticResult.automatedFunding || 0)}`}
                            />
                            <Row label="Number of Manual Deposits" value={statisticResult.numberOfManualFunding || 0} />
                            <Row
                              label="Manual Deposit Amount"
                              value={`₦${fCurrency(statisticResult.manualFunding || 0)}`}
                            />
                            <Row
                              label="Total Number of Deposits"
                              value={
                                (statisticResult.numberOfManualFunding || 0) +
                                (statisticResult.numberOfAutomatedFunding || 0)
                              }
                            />
                            <Row
                              label="Total Deposited Amount"
                              value={`₦${fCurrency(
                                (statisticResult.manualFunding || 0) + (statisticResult.automatedFunding || 0)
                              )}`}
                            />
                          </Stack>
                        </Paper>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 3.5,
                            borderRadius: 3.5,
                            border: '1px solid',
                            borderColor: 'grey.200',
                            height: '100%',
                            backgroundColor: '#ffffff',
                            boxShadow: '0 2px 12px 0 rgba(0, 0, 0, 0.01)',
                          }}
                        >
                          <Typography
                            variant="h5"
                            fontWeight={800}
                            mb={2.5}
                            sx={{ display: 'flex', alignItems: 'center', gap: 1.2, color: 'primary.dark' }}
                          >
                            <TimelineIcon color="primary" /> Transaction Performance
                          </Typography>
                          <Stack divider={<Divider />} spacing={1.5}>
                            <Row label="Processors Utilized" value={statisticResult.processorsUse || 0} />
                            <Row label="Number of Sales" value={status.sales || 0} />
                            <Row label="Successful Transactions" value={status.successTransaction || 0} />
                            <Row label="Failed Transactions" value={status.failedTransactions || 0} />
                            <Row label="Total Volume Processed" value={status.totalTransactions || 0} />
                          </Stack>
                        </Paper>
                      </Grid>

                      <Grid item xs={12}>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 3.5,
                            borderRadius: 3.5,
                            border: '1px solid',
                            borderColor: 'grey.200',
                            backgroundColor: '#ffffff',
                            boxShadow: '0 2px 12px 0 rgba(0, 0, 0, 0.01)',
                          }}
                        >
                          <Typography variant="h5" fontWeight={800} mb={2.5} sx={{ color: 'primary.dark' }}>
                            Sales Distribution
                          </Typography>
                          <Grid container spacing={2.5}>
                            <Grid item xs={12} sm={6} md={4} lg={2.4}>
                              <MiniMetric title="Data Orders" value={`₦${fCurrency(salesBreakDown.DATA_ORDER || 0)}`} />
                            </Grid>
                            <Grid item xs={12} sm={6} md={4} lg={2.4}>
                              <MiniMetric
                                title="Airtime Orders"
                                value={`₦${fCurrency(salesBreakDown.AIRTIME_ORDER || 0)}`}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6} md={4} lg={2.4}>
                              <MiniMetric
                                title="Electricity Orders"
                                value={`₦${fCurrency(salesBreakDown.ELECTRICITY_ORDER || 0)}`}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6} md={6} lg={2.4}>
                              <MiniMetric
                                title="Cable TV Orders"
                                value={`₦${fCurrency(salesBreakDown.CABLE_ORDER || 0)}`}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6} md={6} lg={2.4}>
                              <MiniMetric
                                title="Result Scratch Card"
                                value={`₦${fCurrency(salesBreakDown.RESULT_CHECK_ORDER || 0)}`}
                              />
                            </Grid>
                          </Grid>
                        </Paper>
                      </Grid>

                      {processorRows.length > 0 && (
                        <Grid item xs={12}>
                          <Paper
                            elevation={0}
                            sx={{
                              p: 3.5,
                              borderRadius: 3.5,
                              border: '1px solid',
                              borderColor: 'grey.200',
                              overflow: 'hidden',
                            }}
                          >
                            <Typography
                              variant="h5"
                              fontWeight={800}
                              mb={2.5}
                              sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'primary.dark' }}
                            >
                              <SpeedIcon color="primary" /> Gateway Processor Performance
                            </Typography>
                            <ProcessorReportTable rows={processorRows} />
                          </Paper>
                        </Grid>
                      )}
                    </Grid>
                  </Stack>
                </ComponentToPrint>
              )}
            </Stack>
          )}

          {/* TAB 2: PROFIT & LOSS ANALYSIS WORKSPACE */}
          {activeTab === 'profit' && (
            <Stack spacing={3.5}>
              <Card
                sx={{
                  borderRadius: 3.5,
                  boxShadow: '0 4px 20px 0 rgba(0,0,0,0.04)',
                  border: '1px solid',
                  borderColor: 'grey.200',
                }}
              >
                <Box p={3}>
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    alignItems={{ xs: 'stretch', md: 'center' }}
                    justifyContent="space-between"
                    useFlexGap
                  >
                    <Box>
                      <Typography variant="h6" fontWeight={800}>
                        Profit Calculation Actions
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Audit service-specific net income margins for the chosen range.
                      </Typography>
                    </Box>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} width={{ xs: '100%', sm: 'auto' }}>
                      <Button
                        disabled={profitLoading || !value[0] || !value[1]}
                        onClick={handleCalculateProfit}
                        variant="contained"
                        color="success"
                        startIcon={profitLoading ? <CircularProgress size={20} color="inherit" /> : <PaymentsIcon />}
                        sx={{
                          borderRadius: 2.5,
                          width: { xs: '100%', sm: 180 },
                          height: 48,
                          fontWeight: 700,
                          color: 'white',
                        }}
                      >
                        {profitLoading ? 'Calculating...' : 'Calculate Profit'}
                      </Button>

                      {profitResult && !profitLoading && (
                        <ReactToPrint
                          documentTitle={`ProfitReport-${new Date().toISOString()}`}
                          trigger={() => (
                            <Button
                              variant="contained"
                              color="secondary"
                              startIcon={<PrintIcon />}
                              sx={{ borderRadius: 2.5, width: { xs: '100%', sm: 180 }, height: 48, fontWeight: 700 }}
                            >
                              Print & Download
                            </Button>
                          )}
                          content={() => componentRef.current}
                        />
                      )}
                    </Stack>
                  </Stack>
                </Box>
              </Card>

              {/* Data Load Output */}
              {profitLoading ? (
                <ProfitSkeleton />
              ) : !profitResult ? (
                <EmptyReportState
                  value={value}
                  handleAction={handleCalculateProfit}
                  loading={profitLoading}
                  actionLabel="Calculate Profit"
                  iconColor="success.main"
                  description="To view net profit margins across individual services (Data, Airtime, Electricity, etc.), select a date scope and request profit calculations."
                />
              ) : (
                <ComponentToPrint ref={componentRef}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3.5,
                      borderRadius: 3.5,
                      border: '1px solid',
                      borderColor: 'grey.200',
                      backgroundColor: '#ffffff',
                      boxShadow: '0 4px 20px 0 rgba(0, 0, 0, 0.02)',
                    }}
                  >
                    <Stack spacing={3}>
                      <Box>
                        <Typography variant="h5" fontWeight={800} color="primary.dark">
                          Net Profit Summary
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Reporting Period: {value[0]?.format('DD/MM/YYYY')} - {value[1]?.format('DD/MM/YYYY')}
                        </Typography>
                      </Box>

                      <Paper
                        elevation={0}
                        sx={{
                          p: 3,
                          borderRadius: 3,
                          backgroundColor: 'success.lighter',
                          border: '1px solid',
                          borderColor: 'success.light',
                          position: 'relative',
                        }}
                      >
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography variant="subtitle1" fontWeight={700} color="success.darker">
                            Total Profit Generated
                          </Typography>
                          <Typography variant="h3" fontWeight={900} color="success.dark">
                            ₦{fCurrency(profitResult.totalProfit || 0)}
                          </Typography>
                        </Stack>
                      </Paper>

                      <Typography
                        variant="subtitle2"
                        fontWeight={700}
                        color="text.secondary"
                        sx={{ textTransform: 'uppercase', letterSpacing: '0.5px' }}
                      >
                        Profits by Service Type
                      </Typography>

                      <Grid container spacing={2.5}>
                        {Object.entries(profitResult.services || {}).map(([service, profit]) => (
                          <Grid item xs={12} sm={6} md={4} key={service}>
                            <Paper
                              elevation={0}
                              sx={{
                                p: 2.5,
                                borderRadius: 2.5,
                                border: '1px solid',
                                borderColor: 'grey.200',
                              }}
                            >
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                display="block"
                                mb={0.5}
                                fontWeight={600}
                              >
                                {service}
                              </Typography>
                              <Typography variant="subtitle1" fontWeight={800} color="text.primary">
                                ₦{fCurrency(profit || 0)}
                              </Typography>
                            </Paper>
                          </Grid>
                        ))}
                      </Grid>
                    </Stack>
                  </Paper>
                </ComponentToPrint>
              )}
            </Stack>
          )}

          {/* TAB 3: LEADERBOARD WORKSPACE */}
          {activeTab === 'leaderboard' && (
            <Paper
              elevation={0}
              sx={{
                p: { xs: 3, md: 4 },
                borderRadius: 4,
                border: '1px solid',
                borderColor: 'grey.200',
                boxShadow: '0 4px 20px 0 rgba(0,0,0,0.03)',
              }}
            >
              <Stack spacing={3.5}>
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  justifyContent="space-between"
                  alignItems={{ xs: 'flex-start', md: 'center' }}
                  spacing={2}
                >
                  <Box>
                    <Stack direction="row" spacing={1.5} alignItems="center" mb={0.5}>
                      <EmojiEventsIcon sx={{ color: '#FFC107', fontSize: 32 }} />
                      <Typography variant="h4" fontWeight={800} sx={{ letterSpacing: '-0.5px', color: 'primary.dark' }}>
                        Top Users Leaderboard
                      </Typography>
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                      Analyze leading customer accounts by total transaction volumes or referrals performance.
                    </Typography>
                  </Box>

                  {hasFetchedLeaderboard && leaderboardRows.length > 0 && (
                    <Chip
                      icon={<FilterListIcon />}
                      label={`${leaderboardRows.length} user${leaderboardRows.length !== 1 ? 's' : ''} loaded`}
                      variant="outlined"
                      sx={{ borderRadius: 2, fontWeight: 600 }}
                    />
                  )}
                </Stack>

                <Tabs
                  value={leaderboardType}
                  onChange={(_, newValue) => setLeaderboardType(newValue)}
                  variant={isMobile ? 'scrollable' : 'standard'}
                  scrollButtons={isMobile ? 'auto' : false}
                  allowScrollButtonsMobile
                  sx={{
                    minHeight: 48,
                    borderBottom: 1,
                    borderColor: 'divider',
                    '& .MuiTabs-indicator': {
                      height: 3,
                      borderRadius: 999,
                    },
                  }}
                >
                  <Tab
                    value="transactions"
                    icon={<PeopleAltIcon />}
                    iconPosition="start"
                    label="Transactions Volume Ranking"
                    sx={{
                      textTransform: 'none',
                      fontWeight: 700,
                      minHeight: 48,
                      fontSize: { xs: '0.85rem', sm: '0.95rem' },
                    }}
                  />
                  <Tab
                    value="invites"
                    icon={<PersonAddAlt1Icon />}
                    iconPosition="start"
                    label="Referrals Count Ranking"
                    sx={{
                      textTransform: 'none',
                      fontWeight: 700,
                      minHeight: 48,
                      fontSize: { xs: '0.85rem', sm: '0.95rem' },
                    }}
                  />
                </Tabs>

                {/* Filters Grid */}
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      placeholder="Search by name or email"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleFetchLeaderboard();
                        }
                      }}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2.5 } }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={2}>
                    <FormControl fullWidth>
                      <InputLabel>Period</InputLabel>
                      <Select
                        value={leaderboardPeriod}
                        label="Period"
                        onChange={(e) => setLeaderboardPeriod(e.target.value)}
                        sx={{ borderRadius: 2.5 }}
                      >
                        <MenuItem value="all">All-Time</MenuItem>
                        <MenuItem value="daily">Daily</MenuItem>
                        <MenuItem value="weekly">Weekly</MenuItem>
                        <MenuItem value="monthly">Monthly</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6} md={2}>
                    <FormControl fullWidth>
                      <InputLabel>Sort Order</InputLabel>
                      <Select
                        value={leaderboardSort}
                        label="Sort Order"
                        onChange={(e) => setLeaderboardSort(e.target.value)}
                        sx={{ borderRadius: 2.5 }}
                      >
                        <MenuItem value="desc">Highest First</MenuItem>
                        <MenuItem value="asc">Lowest First</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6} md={1.5}>
                    <FormControl fullWidth>
                      <InputLabel>Show</InputLabel>
                      <Select
                        value={leaderboardLimit}
                        label="Show"
                        onChange={(e) => setLeaderboardLimit(Number(e.target.value))}
                        sx={{ borderRadius: 2.5 }}
                      >
                        <MenuItem value={5}>Top 5</MenuItem>
                        <MenuItem value={10}>Top 10</MenuItem>
                        <MenuItem value={15}>Top 15</MenuItem>
                        <MenuItem value={20}>Top 20</MenuItem>
                        <MenuItem value={30}>Top 30</MenuItem>
                        <MenuItem value={50}>Top 50</MenuItem>
                        <MenuItem value={100}>Top 100</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6} md={1.5}>
                    <Button
                      fullWidth
                      variant="outlined"
                      color="inherit"
                      startIcon={<SwapVertIcon />}
                      sx={{ height: 56, borderRadius: 2.5, fontWeight: 700 }}
                      onClick={handleResetLeaderboardFilters}
                    >
                      Reset
                    </Button>
                  </Grid>

                  <Grid item xs={12} sm={6} md={2}>
                    <Button
                      fullWidth
                      variant="contained"
                      color="primary"
                      startIcon={<SearchIcon />}
                      sx={{ height: 56, borderRadius: 2.5, fontWeight: 700 }}
                      onClick={handleFetchLeaderboard}
                      disabled={leaderboardLoading}
                    >
                      {leaderboardLoading ? 'Loading...' : 'Fetch List'}
                    </Button>
                  </Grid>
                </Grid>

                {/* Leaderboard rows or states */}
                {leaderboardLoading ? (
                  <LeaderboardSkeleton />
                ) : !hasFetchedLeaderboard ? (
                  <EmptyLeaderboardState
                    handleFetchLeaderboard={handleFetchLeaderboard}
                    leaderboardLoading={leaderboardLoading}
                  />
                ) : leaderboardRows.length === 0 ? (
                  <Paper
                    elevation={0}
                    sx={{
                      p: 6,
                      borderRadius: 4,
                      border: '1px dashed',
                      borderColor: 'grey.300',
                      textAlign: 'center',
                      backgroundColor: 'grey.50',
                    }}
                  >
                    <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                      No matching users found
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Try modifying the search term or choosing a broader ranking period.
                    </Typography>
                  </Paper>
                ) : (
                  <Stack spacing={2}>
                    {leaderboardRows?.map((item, index) => {
                      const currentValue = leaderboardType === 'transactions' ? item.totalAmount : item.invites;
                      const progressValue = leaderboardTopValue ? (currentValue / leaderboardTopValue) * 100 : 0;
                      const isInviteTab = leaderboardType === 'invites';

                      const medal = index === 0 ? '🏆' : index === 1 ? '🥈' : index === 2 ? '🥉' : null;
                      const isPodium = index < 3;

                      let bgGradient = 'white';
                      let borderColor = 'grey.200';
                      let shadowGlow = 'none';

                      if (index === 0) {
                        bgGradient = 'linear-gradient(135deg, #FFFDEB 0%, #FFFFFF 100%)';
                        borderColor = '#FFE16A';
                        shadowGlow = '0 8px 24px -6px rgba(255, 193, 7, 0.18)';
                      } else if (index === 1) {
                        bgGradient = 'linear-gradient(135deg, #F8FAFC 0%, #FFFFFF 100%)';
                        borderColor = '#D0D8E8';
                        shadowGlow = '0 8px 24px -6px rgba(148, 163, 184, 0.12)';
                      } else if (index === 2) {
                        bgGradient = 'linear-gradient(135deg, #FAF5F0 0%, #FFFFFF 100%)';
                        borderColor = '#FFE7D9';
                        shadowGlow = '0 8px 24px -6px rgba(183, 33, 54, 0.1)';
                      }

                      return (
                        <Paper
                          key={item.id}
                          elevation={0}
                          onClick={isInviteTab ? () => handleOpenInviteModal(item) : undefined}
                          sx={{
                            p: 3,
                            borderRadius: 3.5,
                            border: '1px solid',
                            borderColor,
                            background: bgGradient,
                            boxShadow: shadowGlow,
                            transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                            cursor: isInviteTab ? 'pointer' : 'default',
                            '&:hover': {
                              borderColor: 'primary.main',
                              transform: 'translateY(-3px)',
                              boxShadow: '0 8px 24px 0 rgba(28, 78, 93, 0.08)',
                            },
                          }}
                        >
                          <Stack spacing={2}>
                            <Stack
                              direction={{ xs: 'column', sm: 'row' }}
                              justifyContent="space-between"
                              alignItems={{ xs: 'flex-start', sm: 'center' }}
                              spacing={2}
                            >
                              <Stack direction="row" spacing={2} alignItems="center">
                                <Chip
                                  icon={
                                    index === 0 ? (
                                      <WorkspacePremiumIcon sx={{ color: '#E0A900 !important' }} />
                                    ) : undefined
                                  }
                                  label={medal ? `${medal} #${index + 1}` : `#${index + 1}`}
                                  color={getRankColor(index)}
                                  sx={{
                                    fontWeight: 800,
                                    minWidth: 70,
                                    height: 30,
                                    borderRadius: 1.5,
                                    boxShadow: isPodium ? '0 2px 8px 0 rgba(0,0,0,0.05)' : 'none',
                                  }}
                                />

                                <Avatar
                                  sx={{
                                    width: 48,
                                    height: 48,
                                    fontWeight: 700,
                                    fontSize: '1.1rem',
                                    bgcolor: isPodium
                                      ? index === 0
                                        ? '#FFE16A'
                                        : index === 1
                                        ? '#DFE3E8'
                                        : '#FFE7D9'
                                      : 'primary.main',
                                    color: isPodium ? 'grey.800' : 'white',
                                    border: '1.5px solid #ffffff',
                                    boxShadow: '0 2px 8px 0 rgba(0,0,0,0.08)',
                                  }}
                                >
                                  {item?.name?.charAt(0)?.toUpperCase() || 'U'}
                                </Avatar>

                                <Box>
                                  <Typography variant="subtitle1" fontWeight={800} color="text.primary">
                                    {item.name}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary" sx={{ opacity: 0.85 }}>
                                    {item.email || item.username || '-'}
                                  </Typography>
                                </Box>
                              </Stack>

                              <Stack
                                direction={{ xs: 'row', sm: 'column' }}
                                spacing={{ xs: 2, sm: 0.5 }}
                                alignItems={{ xs: 'center', sm: 'flex-end' }}
                              >
                                <Typography variant="h5" fontWeight={900} color="primary.dark">
                                  {leaderboardType === 'transactions'
                                    ? `₦${fCurrency(item.totalAmount || 0)}`
                                    : `${item.invites || 0} invites`}
                                </Typography>

                                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                  {leaderboardType === 'transactions'
                                    ? `${item.transactions || 0} transactions processed`
                                    : `${item.invites || 0} referred customers`}
                                </Typography>
                              </Stack>
                            </Stack>

                            <LinearProgress
                              variant="determinate"
                              value={progressValue}
                              sx={{
                                height: 8,
                                borderRadius: 999,
                                backgroundColor: 'grey.100',
                                '& .MuiLinearProgress-bar': {
                                  borderRadius: 999,
                                  backgroundColor:
                                    index === 0
                                      ? '#FFE16A'
                                      : index === 1
                                      ? '#919EAB'
                                      : index === 2
                                      ? '#FF8F6D'
                                      : 'primary.main',
                                },
                              }}
                            />

                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
                              {item.username && (
                                <Chip
                                  size="small"
                                  label={`ID: ${item.username}`}
                                  variant="outlined"
                                  sx={{ borderRadius: 1.5, fontWeight: 500 }}
                                />
                              )}

                              {leaderboardType === 'transactions' ? (
                                <Chip
                                  size="small"
                                  label={`Total Volume: ₦${fCurrency(item.totalAmount || 0)}`}
                                  variant="outlined"
                                  sx={{ borderRadius: 1.5, fontWeight: 500 }}
                                />
                              ) : (
                                <>
                                  <Chip
                                    size="small"
                                    label={`Total Referrals: ${item.invites || 0}`}
                                    variant="outlined"
                                    sx={{ borderRadius: 1.5, fontWeight: 500 }}
                                  />
                                  <Chip
                                    size="small"
                                    color="primary"
                                    label="Click to analyze details"
                                    variant="soft"
                                    sx={{ borderRadius: 1.5, fontWeight: 600 }}
                                  />
                                </>
                              )}
                            </Stack>
                          </Stack>
                        </Paper>
                      );
                    })}
                  </Stack>
                )}
              </Stack>
            </Paper>
          )}

          {/* TAB 4: INACTIVE USERS WORKSPACE */}
          {activeTab === 'inactive' && (
            <Paper
              elevation={0}
              sx={{
                p: { xs: 3, md: 4 },
                borderRadius: 4,
                border: '1px solid',
                borderColor: 'grey.200',
                boxShadow: '0 4px 20px 0 rgba(0,0,0,0.03)',
              }}
            >
              <Stack spacing={3.5}>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  justifyContent="space-between"
                  alignItems={{ xs: 'flex-start', sm: 'center' }}
                  spacing={2}
                >
                  <Box>
                    <Stack direction="row" spacing={1.5} alignItems="center" mb={0.5}>
                      <PeopleAltIcon sx={{ color: 'error.main', fontSize: 32 }} />
                      <Typography variant="h4" fontWeight={800} sx={{ letterSpacing: '-0.5px', color: 'primary.dark' }}>
                        Inactive Users Analysis
                      </Typography>
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                      View and list customer accounts who have not executed transactions for a specified period.
                    </Typography>
                  </Box>

                  <Stack direction="row" spacing={1.5}>
                    {hasFetchedInactive && inactiveTotalCount > 0 && (
                      <Button
                        variant="contained"
                        color="success"
                        startIcon={<CloudDownloadIcon />}
                        onClick={handleOpenExportModal}
                        sx={{ borderRadius: 2, fontWeight: 700, color: 'white' }}
                      >
                        Export Data
                      </Button>
                    )}
                    {hasFetchedInactive && inactiveTotalCount > 0 && (
                      <Chip
                        icon={<FilterListIcon />}
                        label={`${inactiveTotalCount} users found`}
                        variant="outlined"
                        color="error"
                        sx={{ borderRadius: 2, fontWeight: 600 }}
                      />
                    )}
                  </Stack>
                </Stack>

                {/* Filters Grid */}
                <Grid container spacing={2.5} alignItems="center">
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      label="Days of Inactivity"
                      type="number"
                      value={inactiveDays}
                      onChange={(e) => setInactiveDays(Math.max(1, Number(e.target.value)))}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2.5 } }}
                      InputProps={{
                        inputProps: { min: 1 },
                        endAdornment: <InputAdornment position="end">days</InputAdornment>,
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth>
                      <InputLabel>Show Limit</InputLabel>
                      <Select
                        value={inactiveLimit}
                        label="Show Limit"
                        onChange={(e) => setInactiveLimit(Number(e.target.value))}
                        sx={{ borderRadius: 2.5 }}
                      >
                        <MenuItem value={5}>Show 5</MenuItem>
                        <MenuItem value={10}>Show 10</MenuItem>
                        <MenuItem value={20}>Show 20</MenuItem>
                        <MenuItem value={50}>Show 50</MenuItem>
                        <MenuItem value={100}>Show 100</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <Button
                      fullWidth
                      variant="outlined"
                      color="inherit"
                      startIcon={<SwapVertIcon />}
                      sx={{ height: 56, borderRadius: 2.5, fontWeight: 700 }}
                      onClick={handleResetInactiveFilters}
                    >
                      Reset Filters
                    </Button>
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <Button
                      fullWidth
                      variant="contained"
                      color="error"
                      startIcon={<SearchIcon />}
                      sx={{ height: 56, borderRadius: 2.5, fontWeight: 700, color: 'white' }}
                      onClick={handleFetchInactive}
                      disabled={inactiveLoading}
                    >
                      {inactiveLoading ? 'Running...' : 'Fetch List'}
                    </Button>
                  </Grid>
                </Grid>

                {/* Inactive Rows rendering */}
                {inactiveLoading ? (
                  <LeaderboardSkeleton />
                ) : !hasFetchedInactive ? (
                  <EmptyInactiveState handleFetchInactive={handleFetchInactive} inactiveLoading={inactiveLoading} />
                ) : inactiveRows.length === 0 ? (
                  <Paper
                    elevation={0}
                    sx={{
                      p: 6,
                      borderRadius: 4,
                      border: '1px dashed',
                      borderColor: 'grey.300',
                      textAlign: 'center',
                      backgroundColor: 'grey.50',
                    }}
                  >
                    <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                      No inactive users found
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      All registered users have processed transactions within the specified days boundary.
                    </Typography>
                  </Paper>
                ) : (
                  <Stack spacing={2}>
                    {inactiveRows.map((item) => (
                      <Paper
                        key={item.id}
                        elevation={0}
                        sx={{
                          p: 3,
                          borderRadius: 3.5,
                          border: '1px solid',
                          borderColor: 'grey.200',
                          backgroundColor: '#ffffff',
                          transition: 'all 0.25s ease',
                          '&:hover': {
                            borderColor: 'error.main',
                            transform: 'translateY(-2px)',
                            boxShadow: '0 8px 24px 0 rgba(211, 47, 47, 0.05)',
                          },
                        }}
                      >
                        <Stack
                          direction={{ xs: 'column', sm: 'row' }}
                          justifyContent="space-between"
                          alignItems={{ xs: 'flex-start', sm: 'center' }}
                          spacing={2}
                        >
                          <Stack direction="row" spacing={2} alignItems="center">
                            <Avatar
                              sx={{ width: 44, height: 44, bgcolor: 'grey.100', color: 'error.main', fontWeight: 700 }}
                            >
                              {item.firstName ? item.firstName.charAt(0).toUpperCase() : 'U'}
                            </Avatar>
                            <Box>
                              <Typography variant="subtitle1" fontWeight={800} color="text.primary">
                                {[item.firstName, item.lastName].filter(Boolean).join(' ') || item.username || 'User'}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {item.email || '-'} • {item.phone || '-'}
                              </Typography>
                            </Box>
                          </Stack>

                          <Stack alignItems={{ xs: 'flex-start', sm: 'flex-end' }} spacing={0.5}>
                            <Chip
                              label={item.status || 'unknown'}
                              color={item.status === 'active' ? 'success' : 'default'}
                              size="small"
                              sx={{ fontWeight: 700 }}
                            />
                            <Typography variant="caption" color="text.secondary">
                              Registered: {item.createdAt ? new Date(item.createdAt).toLocaleDateString() : '-'}
                            </Typography>
                          </Stack>
                        </Stack>

                        <Stack direction="row" spacing={1} mt={2} flexWrap="wrap" useFlexGap>
                          <Chip
                            size="small"
                            variant="outlined"
                            label={`ID: ${item.username}`}
                            sx={{ borderRadius: 1.5 }}
                          />
                          <Chip
                            size="small"
                            color="error"
                            variant="outlined"
                            label={`Inactive > ${inactiveDays} days`}
                            sx={{ borderRadius: 1.5, fontWeight: 600 }}
                          />
                        </Stack>
                      </Paper>
                    ))}

                    {/* Pagination */}
                    {inactiveTotalCount > inactiveLimit && (
                      <Stack direction="row" justifyContent="center" alignItems="center" spacing={2} mt={3}>
                        <Button
                          disabled={inactivePage === 1 || inactiveLoading}
                          onClick={() => setInactivePage((prev) => prev - 1)}
                          variant="outlined"
                          sx={{ borderRadius: 2 }}
                        >
                          Previous
                        </Button>
                        <Typography variant="body2" color="text.secondary" fontWeight={600}>
                          Page {inactivePage} of {Math.ceil(inactiveTotalCount / inactiveLimit)}
                        </Typography>
                        <Button
                          disabled={inactivePage >= Math.ceil(inactiveTotalCount / inactiveLimit) || inactiveLoading}
                          onClick={() => setInactivePage((prev) => prev + 1)}
                          variant="outlined"
                          sx={{ borderRadius: 2 }}
                        >
                          Next
                        </Button>
                      </Stack>
                    )}
                  </Stack>
                )}
              </Stack>
            </Paper>
          )}
        </Stack>
      </Container>

      {/* REFERRAL DETAILS MODAL */}
      <Dialog
        open={inviteModalOpen}
        onClose={handleCloseInviteModal}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: { borderRadius: 3.5, overflow: 'hidden' },
        }}
      >
        <DialogTitle
          sx={{
            pr: 6,
            pb: 2.5,
            pt: 3,
            background: 'linear-gradient(135deg, rgba(28, 78, 93, 0.05) 0%, rgba(255,255,255,1) 100%)',
            borderBottom: '1px solid',
            borderColor: 'grey.100',
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <EmojiEventsIcon color="warning" />
            <Typography variant="h5" fontWeight={800}>
              Referral Performance Insights
            </Typography>
          </Stack>
          <IconButton onClick={handleCloseInviteModal} sx={{ position: 'absolute', right: 16, top: 20 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 3.5, backgroundColor: 'grey.50' }}>
          {selectedInviteUser ? (
            <Stack spacing={3.5}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: 3,
                  border: '1px solid',
                  borderColor: 'grey.200',
                  backgroundColor: '#ffffff',
                  boxShadow: '0 2px 10px 0 rgba(0,0,0,0.01)',
                }}
              >
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={2.5}
                  alignItems={{ xs: 'flex-start', sm: 'center' }}
                >
                  <Avatar
                    sx={{
                      width: 64,
                      height: 64,
                      fontSize: '1.5rem',
                      fontWeight: 800,
                      bgcolor: 'primary.main',
                      color: 'white',
                    }}
                  >
                    {selectedInviteUser?.name?.charAt(0)?.toUpperCase()}
                  </Avatar>

                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="h5" fontWeight={800} color="text.primary">
                      {selectedInviteUser.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mb={1.5}>
                      {selectedInviteUser.email || selectedInviteUser.username || '-'}
                    </Typography>

                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      <Chip
                        label={`${selectedInviteUser.invites || 0} Total Invites`}
                        color="primary"
                        sx={{ fontWeight: 700 }}
                      />
                      <Chip
                        label={`${inviteModalStats.withTransactions} Active Orders`}
                        color="success"
                        variant="outlined"
                        sx={{ fontWeight: 600 }}
                      />
                      <Chip
                        label={`${inviteModalStats.withoutTransactions} Dormant`}
                        color="warning"
                        variant="outlined"
                        sx={{ fontWeight: 600 }}
                      />
                    </Stack>
                  </Box>
                </Stack>
              </Paper>

              <Box>
                <Typography variant="h6" fontWeight={800} mb={2} color="primary.dark">
                  Referrals Breakdown
                </Typography>

                <Grid container spacing={2} mb={3.5}>
                  <Grid item xs={12} sm={4}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2.5,
                        borderRadius: 3,
                        border: '1px solid',
                        borderColor: 'grey.200',
                        backgroundColor: '#ffffff',
                      }}
                    >
                      <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={0.5}>
                        TOTAL INVITED
                      </Typography>
                      <Typography variant="h4" fontWeight={900} color="primary.main">
                        {inviteModalStats.totalInvited}
                      </Typography>
                    </Paper>
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2.5,
                        borderRadius: 3,
                        border: '1px solid',
                        borderColor: 'success.light',
                        backgroundColor: 'success.lighter',
                      }}
                    >
                      <Typography variant="caption" color="success.darker" fontWeight={600} display="block" mb={0.5}>
                        MADE SERVICE ORDER
                      </Typography>
                      <Typography variant="h4" fontWeight={900} color="success.dark">
                        {inviteModalStats.withTransactions}
                      </Typography>
                    </Paper>
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2.5,
                        borderRadius: 3,
                        border: '1px solid',
                        borderColor: 'warning.light',
                        backgroundColor: 'warning.lighter',
                      }}
                    >
                      <Typography variant="caption" color="warning.darker" fontWeight={600} display="block" mb={0.5}>
                        NO SERVICE ORDER YET
                      </Typography>
                      <Typography variant="h4" fontWeight={900} color="warning.dark">
                        {inviteModalStats.withoutTransactions}
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>

                <Typography variant="h6" fontWeight={800} mb={2} color="primary.dark">
                  Referred Users List
                </Typography>

                {!selectedInviteUser?.invitedUsers?.length ? (
                  <Paper
                    elevation={0}
                    sx={{
                      p: 4,
                      borderRadius: 3,
                      border: '1px dashed',
                      borderColor: 'grey.300',
                      textAlign: 'center',
                      backgroundColor: '#ffffff',
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      No invited users records available.
                    </Typography>
                  </Paper>
                ) : (
                  <Stack spacing={2} sx={{ maxHeight: 380, overflowY: 'auto', pr: 1 }}>
                    {selectedInviteUser.invitedUsers.map((invited) => (
                      <Paper
                        key={invited.id}
                        elevation={0}
                        sx={{
                          p: 2.5,
                          borderRadius: 3,
                          border: '1px solid',
                          borderColor: 'grey.200',
                          backgroundColor: '#ffffff',
                          transition: 'all 0.2s',
                          '&:hover': {
                            borderColor: 'primary.light',
                            boxShadow: '0 4px 12px 0 rgba(0,0,0,0.03)',
                          },
                        }}
                      >
                        <Stack spacing={2}>
                          <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            justifyContent="space-between"
                            alignItems={{ xs: 'flex-start', sm: 'center' }}
                            spacing={2}
                          >
                            <Stack direction="row" spacing={1.5} alignItems="center">
                              <Avatar
                                sx={{
                                  width: 40,
                                  height: 40,
                                  bgcolor: 'grey.100',
                                  color: 'primary.main',
                                  fontWeight: 700,
                                }}
                              >
                                {`${invited?.firstName || invited?.username || 'U'}`.charAt(0).toUpperCase()}
                              </Avatar>

                              <Box>
                                <Typography variant="subtitle2" fontWeight={800}>
                                  {[invited.firstName, invited.lastName].filter(Boolean).join(' ') ||
                                    invited.username ||
                                    'Referred Customer'}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" display="block">
                                  {invited.email || invited.username || '-'}
                                </Typography>
                              </Box>
                            </Stack>

                            <Stack
                              direction={{ xs: 'row', sm: 'column' }}
                              spacing={{ xs: 1.5, sm: 0.5 }}
                              alignItems={{ xs: 'center', sm: 'flex-end' }}
                            >
                              <Typography variant="subtitle2" fontWeight={800} color="primary.dark">
                                ₦{fCurrency(invited?.transactionSummary?.totalAmount || 0)}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" fontWeight={500}>
                                {invited?.transactionSummary?.transactions || 0} transactions
                              </Typography>
                            </Stack>
                          </Stack>

                          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            {invited.username && (
                              <Chip
                                size="small"
                                label={`ID: ${invited.username}`}
                                variant="outlined"
                                sx={{ borderRadius: 1 }}
                              />
                            )}

                            <Chip
                              size="small"
                              color={Number(invited?.transactionSummary?.transactions || 0) > 0 ? 'success' : 'warning'}
                              label={
                                Number(invited?.transactionSummary?.transactions || 0) > 0
                                  ? 'Active Service Client'
                                  : 'No Orders Processed'
                              }
                              variant="outlined"
                              sx={{ borderRadius: 1, fontWeight: 600 }}
                            />

                            <Chip
                              size="small"
                              label={`Spending: ₦${fCurrency(invited?.transactionSummary?.totalAmount || 0)}`}
                              variant="outlined"
                              sx={{ borderRadius: 1 }}
                            />
                          </Stack>

                          <Box sx={{ mt: 0.5, pt: 1, borderTop: '1px solid', borderColor: 'grey.100' }}>
                            <Typography
                              variant="caption"
                              fontWeight={800}
                              color="text.secondary"
                              display="block"
                              mb={1}
                              sx={{ textTransform: 'uppercase', letterSpacing: '0.5px' }}
                            >
                              Recent Transaction Ledger
                            </Typography>

                            {!invited?.recentTransactions?.length ? (
                              <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                No transaction logs found.
                              </Typography>
                            ) : (
                              <List disablePadding>
                                {invited.recentTransactions.map((trx) => (
                                  <ListItem
                                    key={trx.id}
                                    disableGutters
                                    sx={{
                                      py: 1,
                                      borderBottom: '1px solid',
                                      borderColor: 'grey.50',
                                      '&:last-child': { borderBottom: 0 },
                                    }}
                                  >
                                    <ListItemAvatar sx={{ minWidth: 40 }}>
                                      <Avatar
                                        sx={{
                                          width: 28,
                                          height: 28,
                                          bgcolor: 'success.lighter',
                                          color: 'success.dark',
                                          fontSize: '0.8rem',
                                          fontWeight: 800,
                                        }}
                                      >
                                        ₦
                                      </Avatar>
                                    </ListItemAvatar>

                                    <ListItemText
                                      primary={
                                        <Stack
                                          direction="row"
                                          justifyContent="space-between"
                                          alignItems="center"
                                          spacing={2}
                                        >
                                          <Typography variant="body2" fontWeight={700}>
                                            {trx.transactionType || 'Order Payment'}
                                          </Typography>
                                          <Typography variant="body2" fontWeight={800} color="text.primary">
                                            ₦{fCurrency(trx.amount || 0)}
                                          </Typography>
                                        </Stack>
                                      }
                                      secondary={
                                        <Stack
                                          direction="row"
                                          spacing={1}
                                          alignItems="center"
                                          flexWrap="wrap"
                                          useFlexGap
                                          mt={0.5}
                                        >
                                          <Chip
                                            size="small"
                                            label={trx.status || 'unknown'}
                                            color={trx.status === 'success' ? 'success' : 'default'}
                                            variant="outlined"
                                            sx={{ height: 18, fontSize: '0.65rem', borderRadius: 0.5, fontWeight: 700 }}
                                          />
                                          <Typography variant="caption" color="text.secondary">
                                            {trx.createdAt ? new Date(trx.createdAt).toLocaleString() : '-'}
                                          </Typography>
                                        </Stack>
                                      }
                                    />
                                  </ListItem>
                                ))}
                              </List>
                            )}
                          </Box>
                        </Stack>
                      </Paper>
                    ))}
                  </Stack>
                )}
              </Box>
            </Stack>
          ) : null}
        </DialogContent>

        <DialogActions sx={{ p: 2.5, borderTop: '1px solid', borderColor: 'grey.100' }}>
          <Button
            onClick={handleCloseInviteModal}
            variant="contained"
            color="inherit"
            sx={{ borderRadius: 2, fontWeight: 700, px: 3 }}
          >
            Close Details
          </Button>
        </DialogActions>
      </Dialog>

      {/* EXPORT OPTIONS MODAL */}
      <Dialog
        open={exportModalOpen}
        onClose={handleCloseExportModal}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: { borderRadius: 3.5, overflow: 'hidden' },
        }}
      >
        <DialogTitle
          sx={{
            pb: 2.5,
            pt: 3,
            background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.05) 0%, rgba(255,255,255,1) 100%)',
            borderBottom: '1px solid',
            borderColor: 'grey.100',
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <CloudDownloadIcon color="success" />
            <Typography variant="h5" fontWeight={800}>
              Export Inactive Users Wizard
            </Typography>
          </Stack>
          <IconButton onClick={handleCloseExportModal} sx={{ position: 'absolute', right: 16, top: 20 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 3.5 }}>
          {exportLoading ? (
            <Stack py={6} alignItems="center" spacing={2.5}>
              <CircularProgress color="success" />
              <Typography variant="body2" color="text.secondary" fontWeight={600}>
                Fetching all {inactiveTotalCount} matching records for export...
              </Typography>
            </Stack>
          ) : (
            <Stack spacing={3.5}>
              <Box>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="subtitle1" fontWeight={800} color="text.primary">
                    1. Choose Columns to Include
                  </Typography>
                  <Stack direction="row" spacing={1}>
                    <Button
                      size="small"
                      onClick={() => handleSelectAllFields(true)}
                      color="success"
                      sx={{ fontSize: '0.75rem', py: 0.2, px: 1, fontWeight: 700, minWidth: 'auto' }}
                    >
                      Select All
                    </Button>
                    <Button
                      size="small"
                      onClick={() => handleSelectAllFields(false)}
                      color="error"
                      sx={{ fontSize: '0.75rem', py: 0.2, px: 1, fontWeight: 700, minWidth: 'auto' }}
                    >
                      Clear All
                    </Button>
                  </Stack>
                </Stack>
                <Typography variant="caption" color="text.secondary" display="block" mb={2}>
                  Select which properties to download or copy. Check only one (e.g. Phone Number) to copy lists for bulk
                  messaging.
                </Typography>

                <Grid container spacing={1}>
                  {Object.keys(exportSelectedFields).map((field) => (
                    <Grid item xs={6} sm={4} key={field}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={exportSelectedFields[field]}
                            onChange={() => handleCheckboxChange(field)}
                            color="success"
                          />
                        }
                        label={
                          <Typography variant="body2" fontWeight={600} sx={{ textTransform: 'capitalize' }}>
                            {field === 'createdAt' ? 'Joined Date' : field}
                          </Typography>
                        }
                      />
                    </Grid>
                  ))}
                </Grid>
              </Box>

              <Divider />

              <Box>
                <Typography variant="subtitle1" fontWeight={800} gutterBottom color="text.primary">
                  2. Choose Delimiter Format
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" mb={1.5}>
                  Applies only when using the **Copy Formatted List** action.
                </Typography>
                <FormControl component="fieldset">
                  <RadioGroup row value={exportDelimiter} onChange={(e) => setExportDelimiter(e.target.value)}>
                    <FormControlLabel
                      value="comma"
                      control={<Radio color="success" />}
                      label={<Typography variant="body2">Comma Separated (, )</Typography>}
                    />
                    <FormControlLabel
                      value="newline"
                      control={<Radio color="success" />}
                      label={<Typography variant="body2">Newline Separated (\n)</Typography>}
                    />
                  </RadioGroup>
                </FormControl>
              </Box>

              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  backgroundColor: 'success.lighter',
                  border: '1px solid',
                  borderColor: 'success.light',
                }}
              >
                <Typography variant="body2" color="success.darker" fontWeight={700}>
                  Ready to export {exportUsers.length} records.
                </Typography>
              </Paper>
            </Stack>
          )}
        </DialogContent>

        <DialogActions
          sx={{ p: 2.5, borderTop: '1px solid', borderColor: 'grey.100', justifyContent: 'space-between' }}
        >
          <Button onClick={handleCloseExportModal} variant="outlined" color="inherit" sx={{ borderRadius: 2 }}>
            Cancel
          </Button>

          <Stack direction="row" spacing={1.5}>
            <Button
              disabled={exportLoading || exportUsers.length === 0}
              variant="outlined"
              color="success"
              startIcon={<ContentCopyIcon />}
              onClick={handleCopyFormatted}
              sx={{ borderRadius: 2, fontWeight: 700 }}
            >
              Copy List
            </Button>
            <Button
              disabled={exportLoading || exportUsers.length === 0}
              variant="contained"
              color="success"
              startIcon={<CloudDownloadIcon />}
              onClick={handleExportCSV}
              sx={{ borderRadius: 2, fontWeight: 700, color: 'white' }}
            >
              Download CSV
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>
    </Page>
  );
}
